import { useLanguage } from "../App";
import { motion } from "framer-motion";
import { Building2, Shield, Globe2, Award } from "lucide-react";

const aboutContent = {
  fr: {
    title: "À propos",
    subtitle: "Qui sommes-nous ?",
    description: "Documatch est le premier comparateur intelligent de solutions de Gestion Électronique de Documents (GED) en France et en Europe, destiné aux PME, entreprises de taille intermédiaire et organismes publics engagés dans leur transformation numérique.",
    mission: "Sa mission est d'aider les organisations à identifier la solution GED la mieux adaptée à leurs besoins métiers et réglementaires, en intégrant les exigences de conformité au RGPD, de sécurité des données et d'hébergement en Europe.",
    features: [
      { icon: Building2, title: "PME & ETI", desc: "Accompagnement personnalisé pour les entreprises de toutes tailles" },
      { icon: Shield, title: "Conformité RGPD", desc: "Solutions conformes aux réglementations européennes" },
      { icon: Globe2, title: "Hébergement Europe", desc: "Données sécurisées sur le territoire européen" },
      { icon: Award, title: "IA Intelligente", desc: "Recommandations basées sur l'intelligence artificielle" }
    ]
  },
  es: {
    title: "Acerca de",
    subtitle: "¿Quiénes somos?",
    description: "Documatch es el primer comparador inteligente de soluciones de Gestión Electrónica de Documentos (GED) en Francia y Europa, destinado a PYMES, empresas medianas y organismos públicos comprometidos con su transformación digital.",
    mission: "Su misión es ayudar a las organizaciones a identificar la solución GED más adaptada a sus necesidades empresariales y regulatorias, integrando los requisitos de cumplimiento del RGPD, seguridad de datos y alojamiento en Europa.",
    features: [
      { icon: Building2, title: "PYMES", desc: "Acompañamiento personalizado para empresas de todos los tamaños" },
      { icon: Shield, title: "Cumplimiento RGPD", desc: "Soluciones conformes a las regulaciones europeas" },
      { icon: Globe2, title: "Alojamiento Europa", desc: "Datos seguros en territorio europeo" },
      { icon: Award, title: "IA Inteligente", desc: "Recomendaciones basadas en inteligencia artificial" }
    ]
  },
  en: {
    title: "About",
    subtitle: "Who we are",
    description: "Documatch is the first intelligent Document Management System (DMS) comparator in France and Europe, designed for SMEs, mid-sized companies and public organizations engaged in their digital transformation.",
    mission: "Its mission is to help organizations identify the DMS solution best suited to their business and regulatory needs, integrating GDPR compliance requirements, data security and hosting in Europe.",
    features: [
      { icon: Building2, title: "SMEs & Mid-size", desc: "Personalized support for companies of all sizes" },
      { icon: Shield, title: "GDPR Compliance", desc: "Solutions compliant with European regulations" },
      { icon: Globe2, title: "European Hosting", desc: "Secure data on European territory" },
      { icon: Award, title: "Smart AI", desc: "Recommendations based on artificial intelligence" }
    ]
  }
};

export const About = () => {
  const { language } = useLanguage();
  const content = aboutContent[language] || aboutContent.fr;

  return (
    <div className="max-w-7xl mx-auto" data-testid="about-section">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-12"
      >
        <h2 className="font-playfair text-3xl md:text-4xl font-bold text-slate-900 mb-4">
          {content.title}
        </h2>
        <p className="text-slate-600 text-lg">{content.subtitle}</p>
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-12 items-center">
        {/* Text Content */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="space-y-6"
        >
          <p className="text-slate-700 text-lg leading-relaxed">
            {content.description}
          </p>
          <p className="text-slate-600 leading-relaxed">
            {content.mission}
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 gap-4"
        >
          {content.features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow"
            >
              <div className="p-3 bg-blue-100 rounded-xl w-fit mb-3">
                <feature.icon size={24} className="text-blue-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">{feature.title}</h3>
              <p className="text-slate-500 text-sm">{feature.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default About;